import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  FlaskConical,
  Users,
  Trash2,
  TrendingUp,
  Calendar,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getAllBetaTesters,
  getBetaTestingStats,
  deleteBetaTester,
} from "@/api/beta-testing";
import type { BetaTesterWithUser } from "@/types/beta-testing";

const BetaTestersList = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testerToDelete, setTesterToDelete] =
    useState<BetaTesterWithUser | null>(null);

  // Fetch beta testers
  const {
    data: betaTesters,
    isLoading: testersLoading,
    refetch,
  } = useQuery({
    queryKey: ["betaTesters"],
    queryFn: getAllBetaTesters,
  });

  // Fetch beta testing stats
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["betaTestingStats"],
    queryFn: getBetaTestingStats,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (testerId: number) => deleteBetaTester(testerId),
    onSuccess: () => {
      toast({
        title: "Beta Tester Removed",
        description: "Beta tester has been removed from the program.",
      });
      queryClient.invalidateQueries({ queryKey: ["betaTesters"] });
      queryClient.invalidateQueries({ queryKey: ["betaTestingStats"] });
    },
    onError: (error) => {
      console.error("Error deleting beta tester:", error);
      toast({
        title: "Error",
        description: "Failed to remove beta tester.",
        variant: "destructive",
      });
    },
  });

  const handleRefresh = () => {
    refetch();
    refetchStats();
  };

  const handleDeleteClick = (tester: BetaTesterWithUser) => {
    setTesterToDelete(tester);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (testerToDelete) {
      deleteMutation.mutate(testerToDelete.id);
      setDeleteDialogOpen(false);
      setTesterToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTesterToDelete(null);
  };

  const exportBetaTesters = () => {
    if (!betaTesters || betaTesters.length === 0) {
      toast({
        title: "No Data",
        description: "No beta testers found to export.",
        variant: "destructive",
      });
      return;
    }

    const testersData = betaTesters.map((tester) => ({
      email: tester.user.email,
      name: tester.user.name || "No Name",
      joined_date: new Date(tester.joined_at).toLocaleDateString(),
      user_id: tester.user_id,
    }));

    const csvContent = [
      ["Email", "Name", "Joined Date", "User ID"],
      ...testersData.map((row) => [
        row.email,
        row.name,
        row.joined_date,
        row.user_id.toString(),
      ]),
    ]
      .map((row) =>
        row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `beta-testers-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Beta Testers Exported",
      description: `Successfully exported ${testersData.length} beta testers as CSV.`,
    });
  };

  if (testersLoading) {
    return (
      <AdminLayout title="Beta Testers">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Beta Testers">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Beta Testing Program</h1>
            <p className="text-muted-foreground">
              Manage beta testers - users join via mobile app
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={exportBetaTesters}
              disabled={!betaTesters || betaTesters.length === 0}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Export List
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Testers
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_testers}</div>
                <p className="text-xs text-muted-foreground">
                  Joined the program
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Recent Joiners
                </CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.recent_joiners}</div>
                <p className="text-xs text-muted-foreground">Last 7 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Testers
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_testers}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Beta Testers Table */}
        <Card>
          <CardHeader>
            <CardTitle>Beta Testers</CardTitle>
            <CardDescription>
              Users who have joined the beta testing program via mobile app
            </CardDescription>
          </CardHeader>
          <CardContent>
            {betaTesters && betaTesters.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Joined Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {betaTesters.map((tester) => {
                    const joinedDate = new Date(tester.joined_at);
                    const daysSinceJoined = Math.floor(
                      (new Date().getTime() - joinedDate.getTime()) /
                        (1000 * 60 * 60 * 24)
                    );

                    return (
                      <TableRow key={tester.id}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                              {tester.user.avatar_url ? (
                                <img
                                  src={tester.user.avatar_url}
                                  alt={tester.user.name || tester.user.email}
                                  className="h-8 w-8 rounded-full"
                                />
                              ) : (
                                <span className="text-sm font-medium">
                                  {(tester.user.name || tester.user.email)
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="font-medium">
                                {tester.user.name || "No Name"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {tester.user.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {joinedDate.toLocaleDateString()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {daysSinceJoined} days ago
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              daysSinceJoined <= 7 ? "default" : "secondary"
                            }
                          >
                            {daysSinceJoined <= 7 ? "New" : "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <span className="sr-only">Open menu</span>
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                  />
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(tester)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove from Beta Tester
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <FlaskConical className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Beta Testers Yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  Users can join the beta testing program through the mobile
                  app.
                </p>
                <p className="text-sm text-muted-foreground">
                  Once users join, they'll appear here for management.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Remove Beta Tester
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              <strong>
                {testerToDelete?.user.name || testerToDelete?.user.email}
              </strong>{" "}
              from the beta tester?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">This action will:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Remove the user from the beta testing program</li>
                    <li>They will no longer receive beta testing features</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Beta Tester
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default BetaTestersList;
